import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaTruck } from 'react-icons/fa';
import { motion } from 'framer-motion';

const DeliveryPage = () => {
  const api = useApi();
  const [activeDeliveries, setActiveDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchActiveDeliveries = async () => {
      try {
        const response = await api.get('/deliveries/active');
        setActiveDeliveries(response.data.deliveries || []);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to retrieve logistics data.');
        setActiveDeliveries([]);
      } finally {
        setLoading(false);
      }
    };
    fetchActiveDeliveries();
  }, [api]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="min-h-screen bg-deepDark py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 border-b border-white/10 pb-6">
          <h1 className="text-4xl font-serif text-white tracking-wide mb-2 flex items-center gap-4">
            <FaMapMarkerAlt className="text-gold" /> Active Logistics
          </h1>
          <p className="text-[10px] tracking-widest uppercase text-textMuted">Monitor your securing transports in real-time.</p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[10px] tracking-widest uppercase text-textMuted">Locating Transports...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/5 border border-red-500/20 p-8 text-center max-w-lg mx-auto">
            <p className="text-red-500 text-xs tracking-widest uppercase font-bold mb-2">{error}</p>
            <p className="text-textMuted text-xs font-light tracking-wide">Please refresh your connection.</p>
          </div>
        ) : activeDeliveries.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {activeDeliveries.map((delivery, idx) => (
              <motion.div 
                key={delivery._id} 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
                className="bg-surfaceDark border border-white/5 p-8 group hover:border-gold/30 transition-colors flex flex-col h-full"
              >
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/5">
                  <div className="flex items-center gap-3 text-gold text-sm tracking-widest uppercase font-bold">
                    <FaTruck /> {delivery.status}
                  </div>
                  <span className="text-[10px] text-textMuted tracking-widest uppercase">No. {delivery.orderNumber}</span>
                </div>

                <div className="space-y-6 mb-8 flex-1">
                  <div>
                    <p className="text-[10px] text-textMuted uppercase tracking-widest mb-1">Current Coordinates</p>
                    <p className="text-sm font-light text-white">{delivery.currentLocation?.address || 'In Transit'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-textMuted uppercase tracking-widest mb-1">Estimated Arrival</p>
                    <p className="text-sm font-serif text-white">
                      {new Date(delivery.estimatedDeliveryDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-textMuted uppercase tracking-widest mb-1">Last Transmission</p>
                    <p className="text-xs font-light text-white/50">
                      {Math.floor((Date.now() - new Date(delivery.lastUpdate).getTime()) / 60000)} minutes ago
                    </p>
                  </div>
                </div>

                <Link to={`/order/${delivery.orderId}`} className="block w-full text-center bg-transparent border border-white/20 text-white py-4 text-[10px] font-bold tracking-widest uppercase hover:border-gold hover:text-gold transition-colors mt-auto">
                  View Full Details
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="bg-surfaceDark border border-white/5 p-16 text-center max-w-xl mx-auto flex flex-col items-center">
            <div className="w-16 h-16 rounded-full border border-white/10 flex items-center justify-center mb-8">
              <FaMapMarkerAlt className="text-2xl text-gold/50" />
            </div>
            <h2 className="text-2xl font-serif text-white mb-4 tracking-wide">No Active Transports</h2>
            <p className="text-textMuted font-light text-sm mb-10 tracking-wide">There are currently no pieces dispatched to your locations.</p>
            <div className="w-12 h-[1px] bg-gold mb-10"></div>
            <Link to="/profile/orders" className="text-[10px] tracking-widest uppercase text-gold hover:text-white border-b border-gold hover:border-white pb-1 transition-all">
              Review Archive
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default DeliveryPage;
